import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { videoCallAPI } from '../services/api';
import './ActiveCallScreen.css';

// Agora App ID - hardcoded for reliability (no env var issues)
const APP_ID = 'eb9711e75bc94c42ad48328bd1505869';

console.log('Agora App ID configured:', APP_ID);

const ActiveCallScreen = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  
  const [callData, setCallData] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isRemoteVideoOff, setIsRemoteVideoOff] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [isLocalVideoMain, setIsLocalVideoMain] = useState(false); // false = remote is main, true = local is main
  const [networkQuality, setNetworkQuality] = useState(4); // 0-5, 5 is best
  
  const clientRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const remoteUserRef = useRef(null);
  const startTimeRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  const isConnectedRef = useRef(false);

  const handleSwapVideo = () => {
    setIsLocalVideoMain(!isLocalVideoMain);
    
    // Re-render videos in swapped positions
    setTimeout(() => {
      if (isLocalVideoMain) {
        // Swap back: remote to main, local to PIP
        if (localVideoTrackRef.current) {
          localVideoTrackRef.current.play('local-video-player');
        }
        if (remoteUserRef.current && remoteUserRef.current.videoTrack) {
          remoteUserRef.current.videoTrack.play('remote-video-player');
        }
      } else {
        // Swap: local to main, remote to PIP
        if (localVideoTrackRef.current) {
          localVideoTrackRef.current.play('remote-video-player');
        }
        if (remoteUserRef.current && remoteUserRef.current.videoTrack) {
          remoteUserRef.current.videoTrack.play('local-video-player');
        }
      }
    }, 50);
  };

  useEffect(() => {
    initializeCall();
    return () => cleanup();
  }, [callId]);

  // Timer effect
  useEffect(() => {
    if (connectionStatus === 'connected' && !startTimeRef.current) {
      startTimeRef.current = Date.now();
    }

    const timer = setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setCallDuration(elapsed);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [connectionStatus]);

  const initializeCall = async () => {
    try {
      setConnectionStatus('connecting');
      console.log('🚀 [INIT] Starting call initialization...');
      
      // Validate App ID first
      if (!APP_ID || APP_ID === 'undefined' || APP_ID.length < 10) {
        throw new Error('Agora App ID not properly configured');
      }

      console.log('✅ [INIT] Using Agora App ID:', APP_ID);

      // Get call details
      console.log('📞 [INIT] Fetching call data for callId:', callId);
      const response = await videoCallAPI.getActiveCall();
      console.log('✅ [INIT] Call data response:', response);
      console.log('✅ [INIT] Full call data:', JSON.stringify(response.data, null, 2));
      
      if (!response || !response.data) {
        throw new Error('No call data received from server');
      }
      
      const call = response.data;
      
      // Check if call is in a valid state (CALLING or ACTIVE)
      if (call.status !== 'ACTIVE' && call.status !== 'CALLING') {
        throw new Error(`Call is not available. Status: ${call.status}`);
      }
      
      // Sanity check: Make sure caller and receiver are different
      if (call.callerId === call.receiverId) {
        throw new Error('Invalid call: Cannot call yourself');
      }
      
      
      setCallData(call);

      // If we're the receiver and call is ACTIVE, wait a moment for caller to publish
      if (!call.isInitiator && call.status === 'ACTIVE') {
        console.log('⏳ [INIT] Receiver waiting 2 seconds for caller to publish...');
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      console.log('📋 [INIT] Call data:', call);
      console.log('🎬 [INIT] Initializing Agora call:', callId);

      // Create Agora client with better configuration
      console.log('🔧 [INIT] Creating Agora client...');
      const client = AgoraRTC.createClient({ 
        mode: 'rtc', 
        codec: 'vp8',
        cloudProxy: true
      });
      clientRef.current = client;
      console.log('✅ [INIT] Agora client created');
      
      // Set log level for debugging
      AgoraRTC.setLogLevel(0);
      clientRef.current = client;
      console.log('✅ [INIT] Agora client created');

      // Handle remote user
      client.on('user-published', async (user, mediaType) => {
        // Mark as connected
        isConnectedRef.current = true;
        
        // Clear connection timeout since remote user joined
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        
        try {
          await client.subscribe(user, mediaType);
          console.log('✅ [REMOTE] Subscribed to user:', user.uid, mediaType);
          
          if (mediaType === 'video') {
            remoteUserRef.current = user;
            const remoteVideoTrack = user.videoTrack;
            console.log('📺 [REMOTE] Remote video track:', remoteVideoTrack);
            remoteVideoTrack.play('remote-video-player');
            setConnectionStatus('connected');
            setIsRemoteVideoOff(false);
            console.log('✅ [REMOTE] Remote video playing, connection status set to connected');
          }
          
          if (mediaType === 'audio') {
            const remoteAudioTrack = user.audioTrack;
            console.log('🔊 [REMOTE] Remote audio track:', remoteAudioTrack);
            remoteAudioTrack.play();
            console.log('✅ [REMOTE] Remote audio playing');
          }
        } catch (err) {
          console.error('❌ [REMOTE] Failed to subscribe to user:', err);
        }
      });

      client.on('user-unpublished', (user, mediaType) => {
        console.log('👤 [REMOTE] Remote user unpublished:', user.uid, mediaType);
        if (mediaType === 'video') {
          setIsRemoteVideoOff(true);
        }
      });

      client.on('user-left', (user) => {
        console.log('👤 [REMOTE] Remote user left:', user.uid);
        setConnectionStatus('disconnected');
        handleEndCall();
      });

      // Add connection state change listener
      client.on('connection-state-change', (curState, prevState, reason) => {
        console.log(`🔌 [CONNECTION] State changed: ${prevState} -> ${curState}, reason: ${reason}`);
        
        if (curState === 'CONNECTED') {
          console.log('✅ [CONNECTION] Successfully connected to Agora');
        } else if (curState === 'DISCONNECTED') {
          console.log('⚠️ [CONNECTION] Disconnected from Agora');
          setConnectionStatus('disconnected');
        } else if (curState === 'RECONNECTING') {
          console.log('🔄 [CONNECTION] Reconnecting to Agora...');
        }
      });

      // Add network quality listener
      client.on('network-quality', (stats) => {
        console.log('📶 [NETWORK] Quality - Uplink:', stats.uplinkNetworkQuality, 'Downlink:', stats.downlinkNetworkQuality);
        // Use the worse of the two qualities
        const quality = Math.min(stats.uplinkNetworkQuality, stats.downlinkNetworkQuality);
        setNetworkQuality(quality);
      });

      // Add error listener
      client.on('error', (err) => {
        console.error('❌ [ERROR] Agora client error:', err);
        setError(`Connection error: ${err.message || 'Unknown error'}`);
      });
      
      // Add token privilege will expire listener
      client.on('token-privilege-will-expire', async () => {
        console.log('⚠️ [TOKEN] Token will expire soon, refreshing...');
        try {
          const renewUid = call.isInitiator ? call.callerId : call.receiverId;
          const renewChannelName = `call-${callId}`;
          const tokenResponse = await videoCallAPI.getAgoraToken(renewChannelName, parseInt(renewUid, 10));
          await client.renewToken(tokenResponse.data.token);
          console.log('✅ [TOKEN] Token renewed successfully');
        } catch (err) {
          console.error('❌ [TOKEN] Failed to renew token:', err);
        }
      });

      // Join channel (use callId as channel name)
      const uid = call.isInitiator ? call.callerId : call.receiverId;
      const channelName = `call-${callId}`;
      
      // Ensure UID is a number (not string)
      const numericUid = parseInt(uid, 10);
      
      // Get Agora token from backend
      let token = null;
      try {
        console.log('🔑 [TOKEN] Requesting Agora token from backend...');
        const tokenResponse = await videoCallAPI.getAgoraToken(channelName, numericUid);
        token = tokenResponse.data.token;
        console.log('✅ [TOKEN] Received Agora token:', token.substring(0, 20) + '...');
      } catch (tokenError) {
        console.error('❌ [TOKEN] Failed to get Agora token:', tokenError);
        throw new Error('Failed to get authentication token: ' + tokenError.message);
      }
      
      // Check device permissions BEFORE joining
      console.log('🎤 [PERMISSIONS] Checking camera/mic permissions...');
      try {
        const devices = await AgoraRTC.getDevices();
        console.log('📱 [PERMISSIONS] Available devices:', devices);
        
        const cameras = devices.filter(d => d.kind === 'videoinput');
        const mics = devices.filter(d => d.kind === 'audioinput');
        
        console.log('📷 [PERMISSIONS] Cameras found:', cameras.length);
        console.log('🎤 [PERMISSIONS] Microphones found:', mics.length);
        
        if (cameras.length === 0) {
          throw new Error('No camera found on this device');
        }
        if (mics.length === 0) {
          throw new Error('No microphone found on this device');
        }
      } catch (deviceError) {
        console.error('❌ [PERMISSIONS] Device check failed:', deviceError);
        throw new Error('Camera/mic access issue: ' + deviceError.message);
      }
      
      // Join channel
      try {
        console.log('🚪 [JOIN] Attempting to join channel...');
        await client.join(APP_ID, channelName, token, numericUid);
        console.log('✅ [JOIN] Joined channel successfully!');
        
        // Log all remote users already in the channel
        const remoteUsers = client.remoteUsers;
        console.log('👥 [JOIN] Remote users already in channel:', remoteUsers.length);
        
        remoteUsers.forEach(user => {
          console.log('  - User UID:', user.uid, 'hasAudio:', user.hasAudio, 'hasVideo:', user.hasVideo);
        });
        
        // If there are already remote users, subscribe to them immediately
        if (remoteUsers.length > 0) {
          for (const user of remoteUsers) {
            if (user.hasVideo) {
              try {
                await client.subscribe(user, 'video');
                const remoteVideoTrack = user.videoTrack;
                remoteVideoTrack.play('remote-video-player');
                setConnectionStatus('connected');
                
                // Clear timeout since we found the remote user
                if (connectionTimeoutRef.current) {
                  clearTimeout(connectionTimeoutRef.current);
                  connectionTimeoutRef.current = null;
                }
              } catch (err) {
                console.error('Failed to subscribe to existing video:', err);
              }
            }
            if (user.hasAudio) {
              try {
                await client.subscribe(user, 'audio');
                const remoteAudioTrack = user.audioTrack;
                remoteAudioTrack.play();
              } catch (err) {
                console.error('Failed to subscribe to existing audio:', err);
              }
            }
          }
        }
        
      } catch (joinError) {
        console.error('❌ [JOIN] Failed to join channel:', joinError);
        console.error('❌ [JOIN] Error code:', joinError.code);
        console.error('❌ [JOIN] Error message:', joinError.message);
        throw new Error(`Failed to join channel: ${joinError.message} (code: ${joinError.code})`);
      }

      // Create and publish local tracks
      console.log('🎥 [TRACKS] Creating local audio/video tracks...');
      let audioTrack, videoTrack;
      
      try {
        console.log('🎤 [TRACKS] Creating microphone track...');
        audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        console.log('✅ [TRACKS] Microphone track created');
        
        console.log('📷 [TRACKS] Creating camera track...');
        videoTrack = await AgoraRTC.createCameraVideoTrack();
        console.log('✅ [TRACKS] Camera track created');
      } catch (trackError) {
        console.error('❌ [TRACKS] Failed to create tracks:', trackError);
        throw new Error('Failed to access camera/mic: ' + trackError.message);
      }
      
      localAudioTrackRef.current = audioTrack;
      localVideoTrackRef.current = videoTrack;

      // Play local video
      try {
        console.log('📺 [TRACKS] Playing local video...');
        videoTrack.play('local-video-player');
        console.log('✅ [TRACKS] Local video playing');
      } catch (playError) {
        console.error('❌ [TRACKS] Failed to play local video:', playError);
      }

      // Publish tracks
      try {
        console.log('📡 [PUBLISH] Publishing local tracks...');
        await client.publish([audioTrack, videoTrack]);
        console.log('✅ [PUBLISH] Published local tracks successfully!');
        
        if (call.status === 'CALLING') {
          console.log('⏳ [PUBLISH] Call is in CALLING status, waiting for receiver to answer...');
        } else {
          console.log('⏳ [PUBLISH] Waiting for remote user to join...');
        }
        
        // Only set timeout if we haven't already connected to a remote user
        if (connectionStatus === 'connecting') {
          // Set a 60-second timeout for remote user to join (increased from 30)
          connectionTimeoutRef.current = setTimeout(() => {
            if (!isConnectedRef.current) {
              console.log('⏰ [TIMEOUT] Remote user did not join within 60 seconds');
              console.log('⏰ [TIMEOUT] isConnected:', isConnectedRef.current);
              console.log('⏰ [TIMEOUT] Remote users:', client.remoteUsers.length);
              
              if (call.status === 'CALLING') {
                setError('The other person did not answer the call.');
              } else {
                setError('The other person did not join the call. They may have lost connection.');
              }
              setConnectionStatus('timeout');
            } else {
              console.log('⏰ [TIMEOUT] Timer fired but already connected');
            }
          }, 60000); // Increased to 60 seconds
          
          // Also set up a periodic check for remote users (every 2 seconds)
          const checkInterval = setInterval(async () => {
            const remoteUsers = client.remoteUsers;
            console.log('🔍 [CHECK] Periodic check - remote users:', remoteUsers.length);
            
            if (remoteUsers.length > 0 && !isConnectedRef.current) {
              console.log('🔍 [CHECK] Found remote user, subscribing...');
              clearInterval(checkInterval);
              
              // Mark as connected
              isConnectedRef.current = true;
              
              // Clear the timeout immediately
              if (connectionTimeoutRef.current) {
                clearTimeout(connectionTimeoutRef.current);
                connectionTimeoutRef.current = null;
              }
              
              // Subscribe to the remote user
              for (const user of remoteUsers) {
                if (user.hasVideo) {
                  try {
                    await client.subscribe(user, 'video');
                    const remoteVideoTrack = user.videoTrack;
                    if (remoteVideoTrack) {
                      remoteVideoTrack.play('remote-video-player');
                      setConnectionStatus('connected');
                    }
                  } catch (err) {
                    console.error('Failed to subscribe to video:', err);
                  }
                }
                if (user.hasAudio) {
                  try {
                    await client.subscribe(user, 'audio');
                    const remoteAudioTrack = user.audioTrack;
                    if (remoteAudioTrack) {
                      remoteAudioTrack.play();
                    }
                  } catch (err) {
                    console.error('Failed to subscribe to audio:', err);
                  }
                }
              }
            }
          }, 2000);
          
          // Stop checking after 60 seconds
          setTimeout(() => {
            clearInterval(checkInterval);
          }, 60000);
        } else {
          console.log('✅ [PUBLISH] Already connected, skipping timeout');
        }
        
      } catch (publishError) {
        console.error('❌ [PUBLISH] Failed to publish tracks:', publishError);
        throw new Error('Failed to publish video/audio: ' + publishError.message);
      }

    } catch (err) {
      console.error('❌ [ERROR] Failed to initialize call:', err);
      console.error('❌ [ERROR] Error name:', err.name);
      console.error('❌ [ERROR] Error code:', err.code);
      console.error('❌ [ERROR] Error message:', err.message);
      console.error('❌ [ERROR] Full error:', JSON.stringify(err, null, 2));
      
      setConnectionStatus('error');
      
      if (err.name === 'NotAllowedError' || err.code === 'PERMISSION_DENIED') {
        setError('Camera/mic access denied. Please allow permissions in browser settings.');
      } else if (err.name === 'NotFoundError' || err.code === 'DEVICE_NOT_FOUND') {
        setError('No camera/mic found on this device.');
      } else if (err.message && err.message.includes('token')) {
        setError('Authentication failed. Please try again.');
      } else if (err.message && err.message.includes('join')) {
        setError(`Connection failed: ${err.message}`);
      } else if (err.message && err.message.includes('not available')) {
        setError('This call is no longer active.');
      } else {
        setError(`Failed to start call: ${err.message || 'Unknown error'}`);
      }
    }
  };

  const toggleMute = () => {
    if (localAudioTrackRef.current) {
      if (isMuted) {
        localAudioTrackRef.current.setEnabled(true);
      } else {
        localAudioTrackRef.current.setEnabled(false);
      }
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (localVideoTrackRef.current) {
      if (isVideoOff) {
        localVideoTrackRef.current.setEnabled(true);
      } else {
        localVideoTrackRef.current.setEnabled(false);
      }
      setIsVideoOff(!isVideoOff);
    }
  };

  const handleEndCall = async () => {
    console.log('🔴 [END CALL] User ending call:', callId);
    
    // Cleanup Agora first
    cleanup();
    
    // Then end the call in the backend
    try {
      console.log('🔴 [END CALL] Calling API to end call...');
      await videoCallAPI.endCall(callId);
      console.log('✅ [END CALL] Call ended successfully');
    } catch (err) {
      console.error('❌ [END CALL] Failed to end call:', err);
      // Still navigate even if API call fails
    }
    
    // Navigate back
    navigate('/discover');
  };

  const cleanup = () => {
    // Clear connection timeout
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    
    // Close local tracks
    if (localAudioTrackRef.current) {
      localAudioTrackRef.current.close();
    }
    if (localVideoTrackRef.current) {
      localVideoTrackRef.current.close();
    }

    // Leave channel and cleanup
    if (clientRef.current) {
      clientRef.current.leave();
      clientRef.current.removeAllListeners();
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div className="active-call-screen">
        <div className="call-error">
          <div className="error-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <h2>Call Error</h2>
          <p>{error}</p>
          <button className="btn-primary" onClick={() => navigate('/discover')}>
            Back to Browse
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="active-call-screen">
      {/* Main Video Container (Remote by default, can be swapped) */}
      <div className="remote-video-container">
        {connectionStatus === 'connecting' && (
          <div className="connecting-overlay">
            <div className="connecting-spinner"></div>
            <p>
              {callData?.status === 'CALLING' 
                ? 'Calling...' 
                : 'Connecting...'}
            </p>
            {callData?.status === 'CALLING' && (
              <p className="connecting-subtext">Waiting for {callData?.remoteName} to answer</p>
            )}
          </div>
        )}
        <div id="remote-video-player" className="remote-video"></div>
        {connectionStatus === 'connecting' && (
          <div className="no-video-placeholder">
            <div className="avatar-large">
              {callData?.remoteName?.charAt(0) || '?'}
            </div>
            <p>{callData?.remoteName || 'User'}</p>
          </div>
        )}
        {/* Show profile picture when remote camera is off */}
        {isRemoteVideoOff && connectionStatus === 'connected' && (
          <div className="video-off-overlay">
            {callData?.remotePhotoUrl ? (
              <img src={callData.remotePhotoUrl} alt={callData.remoteName} className="video-off-avatar-img" />
            ) : (
              <div className="video-off-avatar">
                {callData?.remoteName?.charAt(0) || '?'}
              </div>
            )}
            <p className="video-off-text">{callData?.remoteName}'s camera is off</p>
          </div>
        )}
      </div>

      {/* Picture-in-Picture Video (Local by default, can be swapped) */}
      <div 
        className="local-video-container"
        onClick={handleSwapVideo}
        title="Click to swap videos"
      >
        <div id="local-video-player" className="local-video"></div>
        {isVideoOff && (
          <div className="video-off-overlay">
            {callData?.remotePhotoUrl ? (
              <img src={callData.remotePhotoUrl} alt={callData.remoteName} className="video-off-avatar-img" />
            ) : (
              <div className="video-off-avatar">
                {callData?.remoteName?.charAt(0) || '?'}
              </div>
            )}
            <p className="video-off-text">Camera is off</p>
          </div>
        )}
        {/* Swap Icon Overlay */}
        <div className="swap-icon-overlay">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
        </div>
      </div>

      {/* Call Info Overlay */}
      <div className="call-info-overlay">
        <div className="call-info-top">
          <div className="caller-info">
            <div className="caller-avatar">
              {callData?.remoteName?.charAt(0) || '?'}
            </div>
            <div className="caller-details">
              <div className="caller-name">
                {callData?.remoteName || 'User'}
              </div>
              <div className="call-status">
                {connectionStatus === 'connected' ? 'Connected' : 'Connecting...'}
              </div>
            </div>
          </div>
          <div className="call-duration">
            {formatDuration(callDuration)}
          </div>
        </div>
      </div>

      {/* Network Quality Indicator */}
      {connectionStatus === 'connected' && (
        <div className="network-quality">
          <div className="network-bars">
            <div className={`network-bar ${networkQuality >= 1 ? 'active' : ''}`}></div>
            <div className={`network-bar ${networkQuality >= 2 ? 'active' : ''}`}></div>
            <div className={`network-bar ${networkQuality >= 3 ? 'active' : ''}`}></div>
            <div className={`network-bar ${networkQuality >= 4 ? 'active' : ''}`}></div>
          </div>
          <span className="network-text">
            {networkQuality >= 4 ? 'Excellent' : networkQuality >= 3 ? 'Good' : networkQuality >= 2 ? 'Fair' : 'Poor'}
          </span>
        </div>
      )}

      {/* Call Controls */}
      <div className="call-controls">
        <button
          className={`control-btn ${isMuted ? 'active' : ''}`}
          onClick={toggleMute}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="1" y1="1" x2="23" y2="23"/>
              <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
              <line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
              <line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
          )}
        </button>

        <button
          className={`control-btn ${isVideoOff ? 'active' : ''}`}
          onClick={toggleVideo}
          title={isVideoOff ? 'Turn Video On' : 'Turn Video Off'}
        >
          {isVideoOff ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 7l-7 5 7 5V7z"/>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
            </svg>
          )}
        </button>

        <button
          className="control-btn end-call-btn"
          onClick={handleEndCall}
          title="End Call"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
        </button>
      </div>

      {/* Credits/rate info removed - video calls are free */}
    </div>
  );
};

export default ActiveCallScreen;
